import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  ChevronDown, ChevronUp, MessageSquare, Bell, Mail, Brain, Globe,
  ArrowRight, ArrowDown, User, Settings, Zap, Radio, Workflow, X,
  ToggleLeft, ToggleRight, Plus, Trash2, Save, MessageCircle, RefreshCw,
  Clock, Hand, Bot, ZoomIn, ZoomOut, RotateCcw, Maximize2, Grid3x3,
  GitBranch, Orbit, Move, Search, Play, Pause, SkipForward, ChevronRight,
  Flame, Eye, EyeOff, Users, Activity, Loader2, Unlink, Power, Maximize, Minimize, LayoutList
} from 'lucide-react';
import { toast } from 'sonner';
import getApiUrl from '../../utils/getApiUrl';

const API = `${getApiUrl()}/api`;
const af = (url, o = {}) => {
  const t = localStorage.getItem("auth_token");
  return fetch(url, { ...o, credentials: "include", headers: { ...(o.headers || {}), ...(t ? { Authorization: `Bearer ${t}` } : {}) } });
};

const CH = {
  sms: { icon: MessageSquare, label: 'SMS', color: '#10B981' },
  whatsapp: { icon: MessageCircle, label: 'WhatsApp', color: '#25D366' },
  email: { icon: Mail, label: 'Email', color: '#6366F1' },
  notification: { icon: Bell, label: 'Notif', color: '#F59E0B' },
  christelle_ia: { icon: Brain, label: 'Christelle IA', color: '#A855F7' },
  webhook: { icon: Globe, label: 'Webhook', color: '#06B6D4' },
  update_field: { icon: Settings, label: 'MAJ', color: '#64748B' },
};
const TGT = { client: 'Client', telepro: 'Télépro', technicien: 'Tech.', manager: 'Manager', commercial: 'Com.', admin: 'Admin', all: 'Tous', system: 'Système' };
const ZONE_C = { telepro: '#3B82F6', r1: '#34C759', r2: '#F59E0B', chantiers: '#EF4444', administratif: '#8B5CF6', compta_fi_new: '#EC4899', amo: '#14B8A6', marketing: '#F97316', calendrier: '#06B6D4', tc_pac_iso: '#6366F1', apres_r1: '#10B981' };
const TRIG_TYPES = {
  auto: { icon: Bot, label: 'Auto', color: '#10B981', bg: '#10B98118' },
  semi_auto: { icon: User, label: 'Semi-auto', color: '#3B82F6', bg: '#3B82F618' },
  manual: { icon: Hand, label: 'Manuel', color: '#F59E0B', bg: '#F59E0B18' },
  conversational: { icon: MessageCircle, label: 'Conv.', color: '#A855F7', bg: '#A855F718' },
};
const SRC_LABELS = { form_submit: 'Formulaire', field_change: 'Champ modifié', api: 'API', cron: 'Planifié', sms_response: 'Rép. SMS', call_result: 'Résultat appel', date_condition: 'Cond. date', whatsapp_response: 'Rép. WhatsApp' };
const LAYOUT_MODES = [
  { id: 'vertical', label: 'Vertical', icon: ArrowDown },
  { id: 'horizontal', label: 'Horizontal', icon: ArrowRight },
  { id: 'radial', label: 'Radial', icon: Orbit },
  { id: 'force', label: 'Force', icon: GitBranch },
];

// ═══════════════════════════════════════
// LAYOUT ALGORITHMS
// ═══════════════════════════════════════
const bfsTiers = (stages) => {
  const tiers = {}; const visited = new Set();
  const queue = [{ id: 'lead', tier: 0 }];
  while (queue.length > 0) {
    const { id, tier } = queue.shift();
    if (visited.has(id)) continue; visited.add(id); tiers[id] = tier;
    const st = stages.find(s => s.stage_id === id);
    if (!st?.workflow?.exit_triggers) continue;
    for (const ex of st.workflow.exit_triggers)
      if (ex.target_stage && !visited.has(ex.target_stage)) queue.push({ id: ex.target_stage, tier: tier + 1 });
  }
  stages.forEach(s => { if (tiers[s.stage_id] === undefined) tiers[s.stage_id] = 6; });
  return tiers;
};

const layoutVertical = (stages) => {
  const tiers = bfsTiers(stages);
  const groups = {};
  stages.forEach(s => { const t = tiers[s.stage_id]; if (!groups[t]) groups[t] = []; groups[t].push(s); });
  const maxT = Math.max(...Object.keys(groups).map(Number), 1);
  const pos = {};
  Object.entries(groups).forEach(([tier, stgs]) => {
    const t = Number(tier); const y = 80 + t * 220;
    const totalW = stgs.length * 190;
    const startX = Math.max(60, (1400 - totalW) / 2);
    stgs.forEach((s, i) => { pos[s.stage_id] = { x: startX + i * 190 + 95, y }; });
  });
  return { pos, w: 1400, h: 140 + (maxT + 1) * 220 };
};
const layoutHorizontal = (stages) => {
  const tiers = bfsTiers(stages);
  const groups = {};
  stages.forEach(s => { const t = tiers[s.stage_id]; if (!groups[t]) groups[t] = []; groups[t].push(s); });
  const maxT = Math.max(...Object.keys(groups).map(Number), 1);
  const pos = {};
  Object.entries(groups).forEach(([tier, stgs]) => {
    const t = Number(tier); const x = 120 + t * 240;
    const totalH = stgs.length * 140;
    const startY = Math.max(60, (900 - totalH) / 2);
    stgs.forEach((s, i) => { pos[s.stage_id] = { x, y: startY + i * 140 + 70 }; });
  });
  return { pos, w: 160 + (maxT + 1) * 240, h: Math.max(900, stages.length * 60) };
};
const layoutRadial = (stages) => {
  const tiers = bfsTiers(stages);
  const groups = {};
  stages.forEach(s => { const t = tiers[s.stage_id]; if (!groups[t]) groups[t] = []; groups[t].push(s); });
  const cx = 700, cy = 650; const pos = {};
  Object.entries(groups).forEach(([tier, stgs]) => {
    const t = Number(tier);
    if (t === 0) { pos[stgs[0].stage_id] = { x: cx, y: cy }; return; }
    const radius = t * 180;
    stgs.forEach((s, i) => {
      const angle = (i / stgs.length) * Math.PI * 2 - Math.PI / 2;
      pos[s.stage_id] = { x: cx + Math.cos(angle) * radius, y: cy + Math.sin(angle) * radius };
    });
  });
  return { pos, w: 1400, h: 1300 };
};
const layoutForce = (stages) => {
  const n = stages.length;
  if (n === 0) return { pos: {}, w: 1400, h: 900 };
  const nodes = {};
  stages.forEach((s, i) => {
    const col = i % 6; const row = Math.floor(i / 6);
    nodes[s.stage_id] = { x: 200 + col * 200 + Math.random() * 40, y: 150 + row * 180 + Math.random() * 40, vx: 0, vy: 0 };
  });
  const edges = [];
  stages.forEach(s => { (s.workflow?.exit_triggers || []).forEach(ex => { if (ex.target_stage && nodes[ex.target_stage]) edges.push({ from: s.stage_id, to: ex.target_stage }); }); });
  const ids = Object.keys(nodes);
  for (let iter = 0; iter < 120; iter++) {
    const alpha = 1 - iter / 120;
    for (let i = 0; i < ids.length; i++) for (let j = i + 1; j < ids.length; j++) {
      const a = nodes[ids[i]], b = nodes[ids[j]];
      let dx = b.x - a.x, dy = b.y - a.y;
      const dist = Math.max(Math.sqrt(dx * dx + dy * dy), 1);
      const force = 25000 / (dist * dist) * alpha;
      const fx = (dx / dist) * force, fy = (dy / dist) * force;
      a.vx -= fx; a.vy -= fy; b.vx += fx; b.vy += fy;
    }
    for (const e of edges) {
      const a = nodes[e.from], b = nodes[e.to];
      let dx = b.x - a.x, dy = b.y - a.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const force = (dist - 200) * 0.02 * alpha;
      const fx = (dx / Math.max(dist, 1)) * force, fy = (dy / Math.max(dist, 1)) * force;
      a.vx += fx; a.vy += fy; b.vx -= fx; b.vy -= fy;
    }
    for (const id of ids) {
      const nd = nodes[id]; nd.vx += (700 - nd.x) * 0.001 * alpha; nd.vy += (500 - nd.y) * 0.001 * alpha;
      nd.vx *= 0.85; nd.vy *= 0.85; nd.x += nd.vx; nd.y += nd.vy;
      nd.x = Math.max(80, Math.min(1320, nd.x)); nd.y = Math.max(80, Math.min(1100, nd.y));
    }
  }
  const pos = {}; for (const id of ids) pos[id] = { x: nodes[id].x, y: nodes[id].y };
  return { pos, w: 1400, h: 1200 };
};
const LAYOUT_FNS = { vertical: layoutVertical, horizontal: layoutHorizontal, radial: layoutRadial, force: layoutForce };

// ═══════════════════════════════════════
// HEATMAP UTILS
// ═══════════════════════════════════════
const heatColor = (count, max) => {
  if (max === 0 || !count) return null;
  const ratio = Math.min(count / max, 1);
  if (ratio < 0.25) return '#10B981';
  if (ratio < 0.5) return '#F59E0B';
  if (ratio < 0.75) return '#F97316';
  return '#EF4444';
};

// BFS path from 'lead' to target
const findPath = (stages, targetId) => {
  const visited = new Set(); const parent = {};
  const queue = ['lead'];
  visited.add('lead');
  while (queue.length > 0) {
    const curr = queue.shift();
    if (curr === targetId) break;
    const st = stages.find(s => s.stage_id === curr);
    if (!st?.workflow?.exit_triggers) continue;
    for (const ex of st.workflow.exit_triggers) {
      if (ex.target_stage && !visited.has(ex.target_stage)) {
        visited.add(ex.target_stage); parent[ex.target_stage] = curr;
        queue.push(ex.target_stage);
      }
    }
  }
  if (!parent[targetId] && targetId !== 'lead') return [];
  const path = []; let cur = targetId;
  while (cur) { path.unshift(cur); cur = parent[cur]; }
  return path;
};

// Get all BFS descendants of a node
const getDescendants = (stages, nodeId) => {
  const desc = new Set(); const queue = [nodeId];
  while (queue.length > 0) {
    const curr = queue.shift();
    const st = stages.find(s => s.stage_id === curr);
    if (!st?.workflow?.exit_triggers) continue;
    for (const ex of st.workflow.exit_triggers) {
      if (ex.target_stage && !desc.has(ex.target_stage) && ex.target_stage !== nodeId) {
        desc.add(ex.target_stage); queue.push(ex.target_stage);
      }
    }
  }
  return desc;
};

// ═══════════════════════════════════════
// BRANCH LABEL
// ═══════════════════════════════════════
const BranchLabel = ({ conn, allStageIds, highlighted, simple }) => {
  const [hovered, setHovered] = useState(false);
  const midX = (conn.x1 + conn.x2) / 2, midY = (conn.y1 + conn.y2) / 2;
  const trigType = TRIG_TYPES[conn.triggerType] || TRIG_TYPES.semi_auto;
  const TrigIcon = trigType.icon;
  const actions = conn.targetActions.filter(a => a.enabled !== false);
  const isBroken = conn.to && !allStageIds.has(conn.to);
  const isHL = highlighted;

  if (simple) return null; // Hide branch labels in simple view

  return (
    <div data-testid={`branch-${conn.from}-${conn.to}`} className="absolute z-20"
      style={{ left: midX, top: midY, transform: 'translate(-50%, -50%)', opacity: isHL ? 1 : undefined }}
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
      <div className={`flex flex-col items-center gap-0.5 transition-all duration-200 ${hovered ? 'scale-110' : ''}`}>
        <div className="flex items-center gap-1 bg-slate-900/90 backdrop-blur-sm border rounded-lg px-2 py-1"
          style={{ borderColor: isHL ? '#A855F7' : `${trigType.color}40`, boxShadow: isHL ? '0 0 12px #A855F740' : undefined }}>
          <div className="w-4 h-4 rounded flex items-center justify-center" style={{ backgroundColor: trigType.bg }}>
            <TrigIcon className="w-2.5 h-2.5" style={{ color: trigType.color }} />
          </div>
          <span className="text-[9px] font-semibold" style={{ color: trigType.color }}>{trigType.label}</span>
          {conn.source && <span className="text-[8px] text-slate-500 border-l border-slate-700 pl-1 ml-0.5">{SRC_LABELS[conn.source] || conn.source}</span>}
          {isBroken && <span className="text-[8px] text-red-400 font-bold ml-1">BROKEN</span>}
        </div>
        {conn.label && <div className="bg-slate-900/80 backdrop-blur-sm border border-slate-700/40 rounded-md px-2 py-0.5 max-w-[160px]"><span className="text-[8px] text-slate-400 block truncate text-center">{conn.label}</span></div>}
        {actions.length > 0 && (
          <div className="flex items-center gap-0.5 bg-slate-900/80 backdrop-blur-sm rounded-full px-1.5 py-0.5 border border-slate-700/30">
            <Zap className="w-2.5 h-2.5 text-emerald-500/60 mr-0.5" />
            {actions.slice(0, 4).map(a => { const ch = CH[a.type]; if (!ch) return null; return <div key={a.id} className="w-4 h-4 rounded-full flex items-center justify-center" style={{ backgroundColor: `${ch.color}25` }} title={`${ch.label} → ${TGT[a.target] || a.target}`}><ch.icon className="w-2.5 h-2.5" style={{ color: ch.color }} /></div>; })}
            {actions.length > 4 && <span className="text-[8px] text-slate-500">+{actions.length - 4}</span>}
          </div>
        )}
        {hovered && (actions.length > 0 || conn.condition) && (
          <div className="absolute top-full mt-1 bg-slate-900/95 backdrop-blur-xl border border-purple-500/30 rounded-lg p-2 shadow-2xl min-w-[200px] max-w-[280px] z-50 pointer-events-none animate-in fade-in duration-150">
            {conn.condition && <div className="mb-1.5 pb-1.5 border-b border-slate-700/40"><span className="text-[9px] text-slate-500 uppercase tracking-wider">Condition</span><p className="text-[10px] text-amber-300 font-mono mt-0.5">{conn.condition}</p></div>}
            {actions.length > 0 && <div><span className="text-[9px] text-slate-500 uppercase tracking-wider">Actions d'entrée cible</span><div className="mt-1 space-y-1">{actions.map(a => { const ch = CH[a.type] || CH.notification; return <div key={a.id} className="flex items-center gap-1.5"><ch.icon className="w-3 h-3 flex-shrink-0" style={{ color: ch.color }} /><span className="text-[9px] font-medium" style={{ color: ch.color }}>{ch.label}</span><span className="text-[8px] text-slate-500">→ {TGT[a.target] || a.target}</span>{a.delay_minutes > 0 && <span className="text-[8px] text-slate-600">+{a.delay_minutes}min</span>}</div>; })}</div></div>}
          </div>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════
// MINIMAP
// ═══════════════════════════════════════
const Minimap = ({ positions, conns, canvasW, canvasH, view, containerW, containerH }) => {
  const mmW = 180, mmH = 120;
  const sc = Math.min(mmW / canvasW, mmH / canvasH);
  const vpX = (-view.x / view.scale) * sc, vpY = (-view.y / view.scale) * sc;
  const vpW = (containerW / view.scale) * sc, vpH = (containerH / view.scale) * sc;
  return (
    <div data-testid="cockpit-minimap" className="absolute bottom-3 right-3 z-40 bg-slate-900/90 backdrop-blur-sm border border-slate-700/50 rounded-lg overflow-hidden" style={{ width: mmW, height: mmH }}>
      <svg width={mmW} height={mmH}>
        {conns.map((c, i) => <line key={i} x1={c.x1 * sc} y1={c.y1 * sc} x2={c.x2 * sc} y2={c.y2 * sc} stroke={c.color} strokeWidth={0.5} opacity={0.4} />)}
        {Object.entries(positions).map(([id, p]) => <circle key={id} cx={p.x * sc} cy={p.y * sc} r={3} fill="#A855F7" opacity={0.7} />)}
        <rect x={vpX} y={vpY} width={vpW} height={vpH} fill="none" stroke="#A855F7" strokeWidth={1} opacity={0.6} rx={2} />
      </svg>
    </div>
  );
};

// ═══════════════════════════════════════
// SIMULATION PANEL
// ═══════════════════════════════════════
const SimPanel = ({ simPath, simStep, merged, onStep, onPlay, onStop, playing }) => {
  const current = simPath[simStep];
  const stage = merged.find(s => s.stage_id === current);
  const wf = stage?.workflow || {};
  const ea = wf.entry_actions || [];
  const ex = wf.exit_triggers || [];
  const nextStage = simPath[simStep + 1];

  return (
    <div data-testid="sim-panel" className="absolute top-3 right-3 z-50 bg-slate-900/95 backdrop-blur-xl border border-cyan-500/40 rounded-xl p-3 shadow-2xl" style={{ width: 280 }}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-cyan-400 animate-pulse" />
          <span className="text-xs font-bold text-cyan-300">Simulation</span>
          <span className="text-[10px] text-slate-500">Étape {simStep + 1}/{simPath.length}</span>
        </div>
        <button onClick={onStop} className="p-1 hover:bg-slate-700 rounded"><X className="w-3.5 h-3.5 text-slate-400" /></button>
      </div>
      {/* Progress bar */}
      <div className="w-full h-1 bg-slate-800 rounded-full mb-2 overflow-hidden">
        <div className="h-full bg-gradient-to-r from-cyan-500 to-purple-500 rounded-full transition-all duration-500" style={{ width: `${((simStep + 1) / simPath.length) * 100}%` }} />
      </div>
      {/* Current stage */}
      <div className="bg-slate-800/60 rounded-lg p-2 mb-2 border border-cyan-500/20">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: stage?.stage_color || '#3B82F6' }} />
          <span className="text-[12px] font-bold text-white">{stage?.stage_name || current}</span>
        </div>
        {ea.length > 0 && (
          <div className="space-y-0.5 mb-1">
            <span className="text-[8px] text-emerald-500 uppercase tracking-wider">Actions exécutées :</span>
            {ea.filter(a => a.enabled !== false).map(a => {
              const ch = CH[a.type] || CH.notification;
              return <div key={a.id} className="flex items-center gap-1 ml-1"><ch.icon className="w-2.5 h-2.5" style={{ color: ch.color }} /><span className="text-[9px]" style={{ color: ch.color }}>{ch.label}</span><span className="text-[8px] text-slate-500">→ {TGT[a.target] || a.target}</span></div>;
            })}
          </div>
        )}
        {nextStage && (
          <div className="mt-1 pt-1 border-t border-slate-700/40">
            <span className="text-[8px] text-amber-500 uppercase tracking-wider">Transition vers :</span>
            <div className="flex items-center gap-1 ml-1 mt-0.5">
              <ArrowRight className="w-2.5 h-2.5 text-amber-400" />
              <span className="text-[9px] text-amber-300">{merged.find(s => s.stage_id === nextStage)?.stage_name || nextStage}</span>
              {ex.find(e => e.target_stage === nextStage) && <span className="text-[8px] text-slate-500">({ex.find(e => e.target_stage === nextStage)?.description})</span>}
            </div>
          </div>
        )}
        {!nextStage && simStep === simPath.length - 1 && <div className="mt-1 pt-1 border-t border-slate-700/40"><span className="text-[9px] text-emerald-400 font-semibold">Fin du parcours</span></div>}
      </div>
      {/* Controls */}
      <div className="flex items-center gap-1.5 justify-center">
        <button disabled={simStep === 0} onClick={() => onStep(-1)} className="px-2 py-1 bg-slate-700 hover:bg-slate-600 rounded text-[10px] text-white disabled:opacity-30 flex items-center gap-0.5"><ChevronRight className="w-3 h-3 rotate-180" />Préc.</button>
        <button onClick={onPlay} className="px-3 py-1 bg-cyan-600 hover:bg-cyan-500 rounded text-[10px] text-white flex items-center gap-0.5">{playing ? <><Pause className="w-3 h-3" />Pause</> : <><Play className="w-3 h-3" />Auto</>}</button>
        <button disabled={simStep >= simPath.length - 1} onClick={() => onStep(1)} className="px-2 py-1 bg-slate-700 hover:bg-slate-600 rounded text-[10px] text-white disabled:opacity-30 flex items-center gap-0.5">Suiv.<SkipForward className="w-3 h-3" /></button>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════
// EXPANDED PANEL + SUB COMPONENTS
// ═══════════════════════════════════════
const ItemRow = ({ item, sec, stage, stages, toggle, del }) => {
  const ch = CH[item.type] || CH.notification; const ChI = ch.icon;
  const targetName = item.target_stage ? (stages.find(x => x.stage_id === item.target_stage)?.stage_name || item.target_stage) : null;
  const trigType = TRIG_TYPES[item.type];
  return (
    <div className={`flex items-start gap-2 p-2 rounded-lg bg-slate-800/50 border border-slate-700/20 ${!item.enabled ? 'opacity-30' : ''}`}>
      <div className="w-6 h-6 rounded-lg flex-shrink-0 flex items-center justify-center mt-0.5" style={{ backgroundColor: `${ch.color}20` }}><ChI className="w-3.5 h-3.5" style={{ color: ch.color }} /></div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[11px] font-semibold" style={{ color: ch.color }}>{ch.label}</span>
          {item.target && <span className="text-[9px] px-1.5 py-0.5 bg-slate-700/50 rounded text-slate-400">{TGT[item.target] || item.target}</span>}
          {item.delay_minutes > 0 && <span className="text-[9px] text-slate-500 flex items-center gap-0.5"><Clock className="w-2.5 h-2.5" />+{item.delay_minutes}min</span>}
          {trigType && <span className="text-[9px] px-1 py-0.5 rounded" style={{ backgroundColor: trigType.bg, color: trigType.color }}>{trigType.label}</span>}
          {targetName && <span className="text-[9px] bg-amber-500/10 border border-amber-500/20 px-1.5 py-0.5 rounded text-amber-400 flex items-center gap-0.5"><ArrowRight className="w-2.5 h-2.5" />{targetName}</span>}
        </div>
        <p className="text-[10px] text-slate-400 mt-0.5 leading-tight">{item.description || item.message || item.condition || '-'}</p>
      </div>
      <div className="flex flex-col gap-0.5 flex-shrink-0">
        <button onClick={() => toggle(stage.stage_id, sec.key, item.id)} className="p-0.5 hover:bg-slate-700 rounded">{item.enabled ? <ToggleRight className="w-4 h-4 text-emerald-400" /> : <ToggleLeft className="w-4 h-4 text-slate-500" />}</button>
        <button onClick={() => del(stage.stage_id, sec.key, item.id)} className="p-0.5 hover:bg-red-500/20 rounded text-slate-600 hover:text-red-400"><Trash2 className="w-3 h-3" /></button>
      </div>
    </div>
  );
};
const AddBtn = ({ sec, secKey, sid, stages, add }) => {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState(sec === 'exits' ? 'semi_auto' : 'sms');
  const [tgt, setTgt] = useState('client');
  const [desc, setDesc] = useState('');
  const [ts, setTs] = useState('');
  if (!open) return <button onClick={() => setOpen(true)} className="w-full py-1.5 border border-dashed border-slate-700/50 rounded-lg text-[10px] text-slate-500 hover:text-purple-400 hover:border-purple-500/30 flex items-center justify-center gap-1"><Plus className="w-3 h-3" /> Ajouter</button>;
  return (
    <div className="bg-slate-800/70 border border-purple-500/30 rounded-lg p-2.5 space-y-2">
      <div className="flex gap-2">
        {sec === 'entry' && <><select value={type} onChange={e => setType(e.target.value)} className="flex-1 px-2 py-1 bg-slate-900 border border-slate-600 rounded text-[10px] text-white">{Object.entries(CH).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select><select value={tgt} onChange={e => setTgt(e.target.value)} className="flex-1 px-2 py-1 bg-slate-900 border border-slate-600 rounded text-[10px] text-white">{Object.entries(TGT).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></>}
        {sec === 'exits' && <select value={ts} onChange={e => setTs(e.target.value)} className="flex-1 px-2 py-1 bg-slate-900 border border-slate-600 rounded text-[10px] text-white"><option value="">-- Étape cible --</option>{stages.map(s => <option key={s.stage_id} value={s.stage_id}>{s.stage_name}</option>)}</select>}
      </div>
      <input type="text" value={desc} onChange={e => setDesc(e.target.value)} placeholder="Description..." className="w-full px-2 py-1 bg-slate-900 border border-slate-600 rounded text-[10px] text-white" />
      <div className="flex gap-1.5 justify-end">
        <button onClick={() => setOpen(false)} className="px-2 py-1 bg-slate-700 rounded text-[9px] text-slate-300">Annuler</button>
        <button onClick={() => { const id = `${sec}_${Date.now()}`; if (sec === 'exits') add(sid, secKey, { id, type: 'semi_auto', source: 'form_submit', description: desc, target_stage: ts, enabled: true }); else if (sec === 'triggers') add(sid, secKey, { id, type, source: 'field_change', description: desc, enabled: true }); else add(sid, secKey, { id, type, target: tgt, message: desc, description: desc, delay_minutes: 0, enabled: true }); setOpen(false); setDesc(''); }} className="px-2 py-1 bg-purple-600 hover:bg-purple-500 rounded text-[9px] text-white flex items-center gap-0.5"><Save className="w-3 h-3" /> OK</button>
      </div>
    </div>
  );
};
const ExpandedPanel = ({ stage, color, ea, et, ex, stages, toggle, del, add, setExpandedNode, editSec, setEditSec }) => (
  <div className="mt-2 bg-slate-900/95 border border-purple-500/30 rounded-xl backdrop-blur-xl shadow-2xl overflow-hidden animate-in slide-in-from-top-2 duration-200"
    style={{ width: '360px', transform: 'translateX(-25%)', zIndex: 100 }} onClick={e => e.stopPropagation()}>
    <div className="p-3 border-b border-slate-700/40 flex items-center justify-between" style={{ background: `linear-gradient(90deg, ${color}15, transparent)` }}>
      <div className="flex items-center gap-2"><Workflow className="w-4 h-4" style={{ color }} /><span className="text-sm font-bold text-white">{stage.stage_name || stage.nom}</span><span className="text-[9px] font-mono text-slate-500">{stage.stage_id}</span></div>
      <button onClick={(e) => { e.stopPropagation(); setExpandedNode(null); }} className="p-1 hover:bg-slate-700 rounded"><X className="w-4 h-4 text-slate-400" /></button>
    </div>
    <div className="max-h-[400px] overflow-y-auto">
      {[{ id: 'entry', key: 'entry_actions', label: "Actions d'entrée", icon: Zap, items: ea, color: '#10B981' },
        { id: 'triggers', key: 'entry_triggers', label: 'Déclencheurs', icon: Radio, items: et, color: '#3B82F6' },
        { id: 'exits', key: 'exit_triggers', label: 'Transitions de sortie', icon: ArrowRight, items: ex, color: '#F59E0B' },
      ].map(sec => {
        const isOpen = editSec === sec.id;
        return (
          <div key={sec.id} className="border-b border-slate-800/50 last:border-0">
            <button onClick={() => setEditSec(isOpen ? null : sec.id)} className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-slate-800/30">
              <div className="flex items-center gap-2"><div className="w-5 h-5 rounded flex items-center justify-center" style={{ backgroundColor: `${sec.color}15` }}><sec.icon className="w-3 h-3" style={{ color: sec.color }} /></div><span className="text-xs font-semibold text-white">{sec.label}</span><span className="text-[10px] px-1.5 py-0.5 bg-slate-700/50 rounded-full text-slate-400">{sec.items.length}</span></div>
              {isOpen ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
            </button>
            {isOpen && <div className="px-3 pb-3 space-y-1.5">
              {sec.items.length === 0 && <p className="text-[10px] text-slate-600 italic text-center py-2">Aucun élément</p>}
              {sec.items.map(item => <ItemRow key={item.id} item={item} sec={sec} stage={stage} stages={stages} toggle={toggle} del={del} />)}
              <AddBtn sec={sec.id} secKey={sec.key} sid={stage.stage_id} stages={stages} add={add} />
            </div>}
          </div>
        );
      })}
    </div>
  </div>
);

// ═══════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════
const WorkflowCockpit2D = () => {
  const [stages, setStages] = useState([]);
  const [etapes, setEtapes] = useState([]);
  const [dashboards, setDashboards] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedZone, setSelectedZone] = useState(null);
  const [expandedNode, setExpandedNode] = useState(null);
  const [editSec, setEditSec] = useState(null);
  const [layoutMode, setLayoutMode] = useState('vertical');
  const [view, setView] = useState({ scale: 1, x: 0, y: 0, rotate: 0 });
  const [customPos, setCustomPos] = useState({});
  const [dragging, setDragging] = useState(null);
  const dragMoved = useRef(false);
  const [panning, setPanning] = useState(null);
  const containerRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ w: 1200, h: 700 });

  // NEW: Feature 1 - Lead counts + Heatmap
  const [leadCounts, setLeadCounts] = useState({});
  const [heatmapOn, setHeatmapOn] = useState(false);

  // NEW: Feature 2 - Search & Focus + Collapse
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightPath, setHighlightPath] = useState(new Set());
  const [collapsedNodes, setCollapsedNodes] = useState(new Set());

  // NEW: Feature 3 - Simulation
  const [simPath, setSimPath] = useState([]);
  const [simStep, setSimStep] = useState(0);
  const [simPlaying, setSimPlaying] = useState(false);
  const simTimerRef = useRef(null);

  // NEW: Feature 4 - Quick Add Stage
  const [showAddStage, setShowAddStage] = useState(false);
  const [newStageData, setNewStageData] = useState({ nom: '', categorie: 'avant_r1', color: '#3B82F6' });
  const [addingSaving, setAddingSaving] = useState(false);
  // NEW: Feature 5 - Edit mode for nodes
  const [editMode, setEditMode] = useState(false);
  const [editingNodeId, setEditingNodeId] = useState(null);
  const [editNodeData, setEditNodeData] = useState({});
  // NEW: Feature 6 - Orphan panel
  const [showOrphanPanel, setShowOrphanPanel] = useState(false);
  // NEW: Feature 7 - Fullscreen
  const [isFullscreen, setIsFullscreen] = useState(false);
  // NEW: Feature 8 - Simplified view
  const [simpleView, setSimpleView] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setContainerSize({ w: e.contentRect.width, h: e.contentRect.height }));
    ro.observe(el); return () => ro.disconnect();
  }, []);

  // Fullscreen via native browser API
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    const onFsChange = () => {
      if (!document.fullscreenElement) setIsFullscreen(false);
    };
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sr, cr, dr, lc] = await Promise.all([
        af(`${API}/pipeline-workflow/stages`).then(r => r.json()),
        af(`${API}/config/pipeline/all`).then(r => r.json()),
        af(`${API}/config/dashboards`).then(r => r.json()),
        af(`${API}/pipeline-workflow/lead-counts`).then(r => r.json()),
      ]);
      setStages(sr.stages || []); setEtapes(cr.etapes || []); setDashboards(dr.dashboards || {});
      setLeadCounts(lc.counts || {});
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => { try { const saved = localStorage.getItem('cockpit_positions'); if (saved) setCustomPos(JSON.parse(saved)); } catch {} }, []);

  const merged = useMemo(() => stages.map(s => {
    const e = etapes.find(p => p.id === s.stage_id) || {};
    const db = e.dashboards?.length > 0 ? e.dashboards : (e.dashboard ? [e.dashboard] : ['telepro']);
    return { ...s, ...e, dashboards: db, workflow: s.workflow };
  }), [stages, etapes]);

  const baseLayout = useMemo(() => (LAYOUT_FNS[layoutMode] || layoutVertical)(merged), [merged, layoutMode]);
  const positions = useMemo(() => {
    const cp = customPos[layoutMode] || {}; const result = {};
    for (const [id, p] of Object.entries(baseLayout.pos)) result[id] = cp[id] || p;
    return result;
  }, [baseLayout, customPos, layoutMode]);

  const allStageIds = useMemo(() => new Set(merged.map(s => s.stage_id)), [merged]);
  const maxLeadCount = useMemo(() => Math.max(...Object.values(leadCounts), 1), [leadCounts]);

  // Hidden nodes (collapsed descendants)
  const hiddenNodes = useMemo(() => {
    const hidden = new Set();
    collapsedNodes.forEach(nodeId => { getDescendants(merged, nodeId).forEach(d => hidden.add(d)); });
    return hidden;
  }, [collapsedNodes, merged]);

  // Search: find matching stage and compute path
  useEffect(() => {
    if (!searchQuery.trim()) { setHighlightPath(new Set()); return; }
    const q = searchQuery.toLowerCase();
    const match = merged.find(s => (s.stage_name || s.nom || '').toLowerCase().includes(q) || s.stage_id.includes(q));
    if (match) {
      const path = findPath(merged, match.stage_id);
      setHighlightPath(new Set(path));
      // Center on the found node
      const p = positions[match.stage_id];
      if (p) {
        const cx = containerSize.w / 2 - p.x * view.scale;
        const cy = containerSize.h / 2 - p.y * view.scale;
        setView(v => ({ ...v, x: cx, y: cy }));
      }
    } else {
      setHighlightPath(new Set());
    }
  }, [searchQuery, merged, positions, containerSize, view.scale]);

  // Simulation auto-play timer
  useEffect(() => {
    if (simPlaying && simPath.length > 0) {
      simTimerRef.current = setInterval(() => {
        setSimStep(prev => {
          if (prev >= simPath.length - 1) { setSimPlaying(false); return prev; }
          return prev + 1;
        });
      }, 1800);
    }
    return () => { if (simTimerRef.current) clearInterval(simTimerRef.current); };
  }, [simPlaying, simPath]);

  const zones = useMemo(() => {
    const z = {};
    merged.forEach(s => {
      const zid = s.dashboards?.[0] || 'telepro';
      if (!z[zid]) z[zid] = { label: '', count: 0, color: ZONE_C[zid] || '#64748B' };
      z[zid].count++;
      const d = dashboards[zid];
      if (d) z[zid].label = (d.label || '').replace(/^[\p{Emoji}\s]+/u, '').trim();
    });
    return z;
  }, [merged, dashboards]);

  const conns = useMemo(() => {
    const arr = [];
    merged.forEach(s => {
      const p1 = positions[s.stage_id];
      if (!p1 || hiddenNodes.has(s.stage_id)) return;
      (s.workflow?.exit_triggers || []).forEach(ex => {
        if (!ex.target_stage || hiddenNodes.has(ex.target_stage)) return;
        const p2 = positions[ex.target_stage];
        const x2 = p2?.x || (p1.x + 120), y2 = p2?.y || (p1.y + 180);
        const targetStage = merged.find(x => x.stage_id === ex.target_stage);
        const targetActions = targetStage?.workflow?.entry_actions || [];
        const zone = s.dashboards?.[0] || 'telepro';
        arr.push({ from: s.stage_id, to: ex.target_stage, x1: p1.x, y1: p1.y, x2, y2, color: ZONE_C[zone] || '#475569', label: ex.description || '', condition: ex.condition || '', source: ex.source || '', triggerType: ex.type || 'semi_auto', enabled: ex.enabled !== false, zone, targetActions, exitId: ex.id });
      });
    });
    return arr;
  }, [merged, positions, hiddenNodes]);

  const brokenCount = conns.filter(c => !allStageIds.has(c.to)).length;

  // Detect orphan stages — stages visually disconnected in the cockpit
  // The graph only renders exit_triggers, not 'next' arrays. So orphans = not reached via exit_triggers.
  const orphanStages = useMemo(() => {
    if (!merged.length) return [];
    // BFS using ONLY exit_triggers (what the graph renders)
    const reachable = new Set();
    const queue = ['lead'];
    const byId = {};
    merged.forEach(s => { byId[s.stage_id] = s; });
    while (queue.length > 0) {
      const sid = queue.shift();
      if (reachable.has(sid)) continue;
      reachable.add(sid);
      const s = byId[sid];
      if (!s) continue;
      (s.workflow?.exit_triggers || []).forEach(ex => {
        if (ex.target_stage && !reachable.has(ex.target_stage)) queue.push(ex.target_stage);
      });
    }
    return merged.filter(s => s.stage_id !== 'lead' && !reachable.has(s.stage_id));
  }, [merged]);

  // Toggle stage actif/inactif
  const toggleStageActif = async (stageId, currentActif) => {
    try {
      await af(`${API}/workflow-ai/execute-action`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action_type: 'update_stage', payload: { stage_id: stageId, updates: { actif: !currentActif } } }),
      });
      toast.success(`${stageId} ${!currentActif ? 'activé' : 'désactivé'}`);
      load();
    } catch { toast.error('Erreur toggle'); }
  };

  // CRUD
  const saveWf = async (sid, wf) => { try { await af(`${API}/pipeline-workflow/stages/${sid}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ stage_id: sid, workflow: wf }) }); setStages(p => p.map(s => s.stage_id === sid ? { ...s, workflow: wf } : s)); toast.success('OK'); } catch { toast.error('Erreur'); } };
  const toggle = (sid, sec, aid) => { const s = stages.find(x => x.stage_id === sid); if (!s) return; const wf = { ...s.workflow }; wf[sec] = wf[sec].map(i => i.id === aid ? { ...i, enabled: !i.enabled } : i); saveWf(sid, wf); };
  const del = (sid, sec, aid) => { const s = stages.find(x => x.stage_id === sid); if (!s) return; const wf = { ...s.workflow }; wf[sec] = wf[sec].filter(i => i.id !== aid); saveWf(sid, wf); };
  const add = (sid, sec, a) => { const s = stages.find(x => x.stage_id === sid); if (!s) return; const wf = { ...s.workflow }; wf[sec] = [...(wf[sec] || []), a]; saveWf(sid, wf); };

  // Quick add stage via API
  const quickAddStage = async () => {
    if (!newStageData.nom.trim()) return;
    setAddingSaving(true);
    const id = newStageData.nom.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
    try {
      await af(`${API}/workflow-ai/execute-action`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action_type: 'add_stages', payload: { stages: [{ id, nom: newStageData.nom, color: newStageData.color, categorie: newStageData.categorie, ordre: merged.length + 1, next: [] }] } }),
      });
      setShowAddStage(false); setNewStageData({ nom: '', categorie: 'avant_r1', color: '#3B82F6' });
      toast.success(`Étape "${newStageData.nom}" ajoutée`); load();
    } catch { toast.error('Erreur'); }
    setAddingSaving(false);
  };

  // Quick edit stage name/color
  const quickEditStage = async () => {
    if (!editingNodeId || !editNodeData.nom?.trim()) return;
    try {
      await af(`${API}/workflow-ai/execute-action`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action_type: 'update_stage', payload: { stage_id: editingNodeId, updates: editNodeData } }),
      });
      setEditingNodeId(null); toast.success('Étape modifiée'); load();
    } catch { toast.error('Erreur'); }
  };

  const quickDeleteStage = async (sid) => {
    try {
      await af(`${API}/workflow-ai/execute-action`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action_type: 'delete_stage', payload: { stage_id: sid } }),
      });
      setEditingNodeId(null); setExpandedNode(null); toast.success('Étape supprimée'); load();
    } catch { toast.error('Erreur'); }
  };

  // View handlers
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setView(v => {
      const ns = Math.max(0.2, Math.min(3, v.scale * delta));
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) { const mx = e.clientX - rect.left, my = e.clientY - rect.top; const ratio = ns / v.scale; return { ...v, scale: ns, x: mx - (mx - v.x) * ratio, y: my - (my - v.y) * ratio }; }
      return { ...v, scale: ns };
    });
  }, []);
  const handleCanvasMouseDown = useCallback((e) => {
    if (e.target.closest('[data-node]') || e.target.closest('[data-panel]')) return;
    setPanning({ startX: e.clientX, startY: e.clientY, viewStartX: view.x, viewStartY: view.y });
  }, [view.x, view.y]);
  const handleMouseMove = useCallback((e) => {
    if (dragging) {
      const dx = (e.clientX - dragging.startX) / view.scale, dy = (e.clientY - dragging.startY) / view.scale;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) dragMoved.current = true;
      setCustomPos(prev => { const mp = { ...(prev[layoutMode] || {}) }; mp[dragging.id] = { x: dragging.nodeStartX + dx, y: dragging.nodeStartY + dy }; const next = { ...prev, [layoutMode]: mp }; try { localStorage.setItem('cockpit_positions', JSON.stringify(next)); } catch {} return next; });
    } else if (panning) {
      setView(v => ({ ...v, x: panning.viewStartX + (e.clientX - panning.startX), y: panning.viewStartY + (e.clientY - panning.startY) }));
    }
  }, [dragging, panning, view.scale, layoutMode]);
  const handleMouseUp = useCallback(() => { setDragging(null); setPanning(null); }, []);
  const handleNodeDragStart = useCallback((e, stageId) => {
    e.stopPropagation(); const p = positions[stageId]; if (!p) return;
    dragMoved.current = false; setDragging({ id: stageId, startX: e.clientX, startY: e.clientY, nodeStartX: p.x, nodeStartY: p.y });
  }, [positions]);
  const resetView = useCallback(() => setView({ scale: 1, x: 0, y: 0, rotate: 0 }), []);
  const resetPositions = useCallback(() => { setCustomPos(prev => { const next = { ...prev }; delete next[layoutMode]; localStorage.setItem('cockpit_positions', JSON.stringify(next)); return next; }); }, [layoutMode]);

  useEffect(() => { const el = containerRef.current; if (!el) return; el.addEventListener('wheel', handleWheel, { passive: false }); return () => el.removeEventListener('wheel', handleWheel); }, [handleWheel]);

  // Simulation: build path from 'lead' following first enabled exit
  const startSim = useCallback(() => {
    const path = ['lead']; const visited = new Set(['lead']);
    let cur = 'lead';
    for (let i = 0; i < 30; i++) {
      const st = merged.find(s => s.stage_id === cur);
      if (!st?.workflow?.exit_triggers) break;
      const next = st.workflow.exit_triggers.find(e => e.enabled !== false && e.target_stage && !visited.has(e.target_stage));
      if (!next) break;
      visited.add(next.target_stage); path.push(next.target_stage); cur = next.target_stage;
    }
    setSimPath(path); setSimStep(0); setSimPlaying(false);
    toast.success(`Simulation : ${path.length} étapes`);
  }, [merged]);
  const stopSim = useCallback(() => { setSimPath([]); setSimStep(0); setSimPlaying(false); }, []);

  // Collapse toggle
  const toggleCollapse = useCallback((nodeId) => {
    setCollapsedNodes(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId); else next.add(nodeId);
      return next;
    });
  }, []);

  if (loading) return <div className="flex items-center justify-center py-20"><div className="animate-spin w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full" /></div>;

  const inZone = (s) => !selectedZone || s.dashboards?.includes(selectedZone);
  const connInZone = (c) => !selectedZone || c.zone === selectedZone;
  const isSimActive = simPath.length > 0;
  const simPathSet = new Set(simPath);
  const simConnSet = new Set(simPath.slice(0, -1).map((s, i) => `${s}->${simPath[i + 1]}`));
  const totalLeads = Object.values(leadCounts).reduce((a, b) => a + b, 0);

  return (
    <div data-testid="cockpit-ecosystem" className="space-y-3">
      {/* HEADER */}
      <div className="bg-gradient-to-r from-slate-800/80 to-purple-900/20 border border-slate-700/50 rounded-2xl p-4">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h2 className="text-lg font-bold text-white flex items-center gap-2"><Workflow className="w-5 h-5 text-purple-400" /> Cockpit Workflow</h2>
            <p className="text-xs text-slate-400">
              {merged.length} étapes - {conns.length} branches
              {totalLeads > 0 && <span className="text-cyan-400 ml-2"><Users className="w-3 h-3 inline mr-0.5" />{totalLeads} leads</span>}
              {brokenCount > 0 && <span className="text-red-400 font-semibold ml-2">{brokenCount} lien(s) cassé(s)</span>}
            </p>
          </div>
          <button onClick={load} data-testid="cockpit-refresh" className="px-3 py-1.5 bg-slate-700/50 hover:bg-slate-600/50 rounded-lg text-slate-400 text-xs flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Rafraîchir</button>
        </div>
        {/* TOOLBAR ROW 1 */}
        <div className="flex items-center gap-2 flex-wrap mb-2">
          {/* Layout modes */}
          <div className="flex items-center gap-1 bg-slate-800/60 rounded-lg p-0.5">
            {LAYOUT_MODES.map(m => { const LI = m.icon; return <button key={m.id} data-testid={`layout-${m.id}`} onClick={() => { setLayoutMode(m.id); setView(v => ({ ...v, scale: 1, x: 0, y: 0 })); }} className={`px-2 py-1.5 rounded-md text-[10px] font-medium flex items-center gap-1 transition-all ${layoutMode === m.id ? 'bg-purple-600/80 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-700/50'}`}><LI className="w-3 h-3" />{m.label}</button>; })}
          </div>
          {/* Zoom */}
          <div className="flex items-center gap-1 bg-slate-800/60 rounded-lg p-0.5">
            <button onClick={() => setView(v => ({ ...v, scale: Math.max(0.2, v.scale - 0.15) }))} className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-slate-700/50"><ZoomOut className="w-3.5 h-3.5" /></button>
            <span className="text-[10px] text-slate-400 min-w-[32px] text-center">{Math.round(view.scale * 100)}%</span>
            <button onClick={() => setView(v => ({ ...v, scale: Math.min(3, v.scale + 0.15) }))} className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-slate-700/50"><ZoomIn className="w-3.5 h-3.5" /></button>
          </div>
          {/* Rotation */}
          <div className="flex items-center gap-1 bg-slate-800/60 rounded-lg px-2 py-1">
            <RotateCcw className="w-3 h-3 text-slate-500" />
            <input data-testid="rotation-slider" type="range" min="-180" max="180" value={view.rotate} onChange={e => setView(v => ({ ...v, rotate: Number(e.target.value) }))} className="w-16 h-1 accent-purple-500 cursor-pointer" />
            <span className="text-[10px] text-slate-400 min-w-[24px]">{view.rotate}°</span>
          </div>
          {/* Resets */}
          <button onClick={resetView} className="px-2 py-1.5 bg-slate-800/60 rounded-lg text-[10px] text-slate-400 hover:text-white hover:bg-slate-700/50 flex items-center gap-1"><Maximize2 className="w-3 h-3" />Vue</button>
          <button onClick={resetPositions} className="px-2 py-1.5 bg-slate-800/60 rounded-lg text-[10px] text-slate-400 hover:text-white hover:bg-slate-700/50 flex items-center gap-1"><Grid3x3 className="w-3 h-3" />Positions</button>
          <div className="w-px h-5 bg-slate-700" />
          {/* Heatmap toggle */}
          <button data-testid="heatmap-toggle" onClick={() => setHeatmapOn(h => !h)}
            className={`px-2.5 py-1.5 rounded-lg text-[10px] font-medium flex items-center gap-1 transition-all ${heatmapOn ? 'bg-orange-600/80 text-white' : 'bg-slate-800/60 text-slate-400 hover:text-white hover:bg-slate-700/50'}`}>
            <Flame className="w-3 h-3" />Heatmap
          </button>
          {/* Simulate */}
          <button data-testid="sim-start" onClick={isSimActive ? stopSim : startSim}
            className={`px-2.5 py-1.5 rounded-lg text-[10px] font-medium flex items-center gap-1 transition-all ${isSimActive ? 'bg-cyan-600/80 text-white animate-pulse' : 'bg-slate-800/60 text-slate-400 hover:text-white hover:bg-slate-700/50'}`}>
            {isSimActive ? <><Pause className="w-3 h-3" />Stop Sim</> : <><Play className="w-3 h-3" />Simuler</>}
          </button>
          {/* Collapse all / expand */}
          <button onClick={() => setCollapsedNodes(new Set())} className="px-2 py-1.5 bg-slate-800/60 rounded-lg text-[10px] text-slate-400 hover:text-white hover:bg-slate-700/50 flex items-center gap-1"><Eye className="w-3 h-3" />Tout</button>
          <div className="w-px h-5 bg-slate-700" />
          {/* Edit mode toggle */}
          <button data-testid="edit-mode-toggle" onClick={() => setEditMode(e => !e)}
            className={`px-2.5 py-1.5 rounded-lg text-[10px] font-medium flex items-center gap-1 transition-all ${editMode ? 'bg-violet-600/80 text-white' : 'bg-slate-800/60 text-slate-400 hover:text-white hover:bg-slate-700/50'}`}>
            <Settings className="w-3 h-3" />{editMode ? 'Édition ON' : 'Éditer'}
          </button>
          {/* Add stage */}
          <button data-testid="cockpit-add-stage" onClick={() => setShowAddStage(s => !s)}
            className={`px-2.5 py-1.5 rounded-lg text-[10px] font-medium flex items-center gap-1 transition-all ${showAddStage ? 'bg-emerald-600/80 text-white' : 'bg-slate-800/60 text-slate-400 hover:text-white hover:bg-slate-700/50'}`}>
            <Plus className="w-3 h-3" />Ajouter
          </button>
          {/* Orphan panel toggle */}
          {orphanStages.length > 0 && (
            <button data-testid="orphan-panel-toggle" onClick={() => setShowOrphanPanel(p => !p)}
              className={`px-2.5 py-1.5 rounded-lg text-[10px] font-medium flex items-center gap-1 transition-all ${showOrphanPanel ? 'bg-amber-600/80 text-white' : 'bg-slate-800/60 text-amber-400 hover:text-white hover:bg-slate-700/50'}`}>
              <Unlink className="w-3 h-3" />Orphelins ({orphanStages.length})
            </button>
          )}
          {/* Fullscreen toggle */}
          <button data-testid="fullscreen-toggle" onClick={toggleFullscreen}
            className={`px-2.5 py-1.5 rounded-lg text-[10px] font-medium flex items-center gap-1 transition-all ${isFullscreen ? 'bg-cyan-600/80 text-white' : 'bg-slate-800/60 text-slate-400 hover:text-white hover:bg-slate-700/50'}`}>
            {isFullscreen ? <Minimize className="w-3 h-3" /> : <Maximize className="w-3 h-3" />}{isFullscreen ? 'Quitter (ESC)' : 'Plein écran'}
          </button>
          {/* Simplified view toggle */}
          <button data-testid="simple-view-toggle" onClick={() => setSimpleView(p => !p)}
            className={`px-2.5 py-1.5 rounded-lg text-[10px] font-medium flex items-center gap-1 transition-all ${simpleView ? 'bg-purple-600/80 text-white' : 'bg-slate-800/60 text-slate-400 hover:text-white hover:bg-slate-700/50'}`}>
            <LayoutList className="w-3 h-3" />{simpleView ? 'Vue détaillée' : 'Vue simple'}
          </button>
        </div>
        {/* TOOLBAR ROW 2 - Search + Zones */}
        <div className="flex items-center gap-3 flex-wrap">
          {/* Search */}
          <div className="relative">
            <Search className="w-3 h-3 text-slate-500 absolute left-2 top-1/2 -translate-y-1/2" />
            <input data-testid="cockpit-search" type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              placeholder="Rechercher une étape..."
              className="pl-6 pr-6 py-1.5 bg-slate-800/60 border border-slate-700/40 rounded-lg text-[11px] text-white w-56 focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/20 outline-none" />
            {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 hover:bg-slate-700 rounded"><X className="w-3 h-3 text-slate-500" /></button>}
          </div>
          {highlightPath.size > 0 && <span className="text-[10px] text-purple-400">{highlightPath.size} étape(s) dans le chemin</span>}
          {/* Zone filter */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] text-slate-500 uppercase tracking-wider">Zones:</span>
            <button onClick={() => setSelectedZone(null)} className={`px-2 py-1 rounded-full text-[10px] font-medium ${!selectedZone ? 'bg-white/10 text-white ring-1 ring-white/20' : 'bg-slate-700/30 text-slate-500'}`}>Tout</button>
            {Object.entries(zones).map(([id, z]) => (
              <button key={id} onClick={() => setSelectedZone(selectedZone === id ? null : id)}
                className={`px-2 py-1 rounded-full text-[10px] font-medium flex items-center gap-1 transition-all ${selectedZone === id ? 'ring-1' : selectedZone ? 'opacity-30' : ''}`}
                style={{ backgroundColor: `${z.color}20`, color: z.color }}>
                <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: z.color }} />{z.label || id} ({z.count})
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* QUICK ADD STAGE PANEL */}
      {showAddStage && (
        <div data-testid="cockpit-add-stage-form" className="bg-slate-900/90 backdrop-blur-sm border border-emerald-500/30 rounded-xl p-4 flex items-end gap-3">
          <div className="flex-1 space-y-1">
            <label className="text-[10px] text-slate-500 uppercase tracking-wider">Nom</label>
            <input value={newStageData.nom} onChange={e => setNewStageData(p => ({ ...p, nom: e.target.value }))}
              placeholder="Nom de la nouvelle étape" data-testid="cockpit-new-stage-name"
              className="w-full bg-slate-800/60 border border-slate-700/40 rounded-lg px-3 py-2 text-xs text-white placeholder:text-slate-600 focus:outline-none focus:ring-1 focus:ring-emerald-500/50" />
          </div>
          <div className="space-y-1">
            <label className="text-[10px] text-slate-500 uppercase tracking-wider">Catégorie</label>
            <select value={newStageData.categorie} onChange={e => setNewStageData(p => ({ ...p, categorie: e.target.value }))}
              className="bg-slate-800/60 border border-slate-700/40 rounded-lg px-2 py-2 text-xs text-white focus:outline-none">
              <option value="avant_r1">Avant R1</option><option value="apres_r1">Après R1</option><option value="final">Final</option>
            </select>
          </div>
          <div className="flex gap-1">
            {['#3B82F6','#8B5CF6','#10B981','#F59E0B','#EF4444','#06B6D4'].map(c => (
              <button key={c} onClick={() => setNewStageData(p => ({ ...p, color: c }))}
                className={`w-7 h-7 rounded-lg border-2 transition-all ${newStageData.color === c ? 'border-white scale-110' : 'border-transparent'}`}
                style={{ backgroundColor: c }} />
            ))}
          </div>
          <button onClick={quickAddStage} disabled={!newStageData.nom.trim() || addingSaving}
            data-testid="cockpit-confirm-add"
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 text-white text-xs font-semibold rounded-lg transition-all flex items-center gap-1">
            {addingSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}Ajouter
          </button>
          <button onClick={() => setShowAddStage(false)} className="px-3 py-2 bg-slate-700 text-slate-400 text-xs rounded-lg hover:bg-slate-600">Annuler</button>
        </div>
      )}

      {/* CANVAS */}
      <div ref={containerRef} className="relative rounded-2xl border border-slate-700/30 overflow-hidden bg-slate-950"
        style={{ height: isFullscreen ? '100vh' : '70vh', cursor: panning ? 'grabbing' : (dragging ? 'grabbing' : 'grab') }}
        onMouseDown={handleCanvasMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp}>

        {/* Fullscreen header bar */}
        {isFullscreen && (
          <div className="absolute top-0 left-0 right-0 z-[60] flex items-center justify-between px-4 py-2 bg-slate-900/90 backdrop-blur-sm border-b border-slate-700/30">
            <div className="flex items-center gap-3">
              <Workflow className="w-4 h-4 text-purple-400" />
              <span className="text-sm font-bold text-white">Cockpit Workflow</span>
              <span className="text-xs text-slate-400">{merged.length} étapes - {conns.length} branches</span>
            </div>
            <button data-testid="exit-fullscreen" onClick={toggleFullscreen}
              className="px-3 py-1.5 bg-slate-700/60 hover:bg-red-600/60 rounded-lg text-xs text-slate-300 hover:text-white flex items-center gap-1.5 transition-all">
              <Minimize className="w-3.5 h-3.5" />ESC pour quitter
            </button>
          </div>
        )}

        <div style={{
          transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale}) rotate(${view.rotate}deg)`,
          transformOrigin: '0 0', transition: dragging || panning ? 'none' : 'transform 0.3s ease',
          minWidth: `${baseLayout.w}px`, minHeight: `${baseLayout.h}px`, position: 'relative',
          background: 'radial-gradient(ellipse at 50% 10%, #1a1040 0%, #0f172a 40%, #020617 100%)',
        }}>
          <div className="absolute inset-0 opacity-[0.025]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.4) 1px, transparent 1px)', backgroundSize: '80px 80px' }} />

          {/* SVG */}
          <svg className="absolute inset-0 w-full h-full" style={{ zIndex: 1, overflow: 'visible' }}>
            <defs>
              {Object.entries(ZONE_C).map(([id, c]) => <marker key={id} id={`a-${id}`} markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto"><path d="M0,0 L10,3.5 L0,7" fill="none" stroke={c} strokeWidth="1.5" /></marker>)}
              <marker id="a-broken" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto"><path d="M0,0 L10,3.5 L0,7" fill="none" stroke="#EF4444" strokeWidth="1.5" /></marker>
              <marker id="a-sim" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto"><path d="M0,0 L10,3.5 L0,7" fill="none" stroke="#22D3EE" strokeWidth="1.5" /></marker>
            </defs>
            {conns.map((c, i) => {
              const active = connInZone(c);
              const isBroken = !allStageIds.has(c.to);
              const isSimConn = isSimActive && simConnSet.has(`${c.from}->${c.to}`);
              const isHL = highlightPath.size > 0 && highlightPath.has(c.from) && highlightPath.has(c.to);
              const dx = c.x2 - c.x1, dy = c.y2 - c.y1;
              const cpOff = Math.min(Math.abs(dx) * 0.3, 80);
              const path = `M ${c.x1} ${c.y1 + 20} C ${c.x1 + (dx > 0 ? cpOff : -cpOff) * 0.2} ${c.y1 + dy * 0.4}, ${c.x2 - (dx > 0 ? cpOff : -cpOff) * 0.2} ${c.y2 - dy * 0.4}, ${c.x2} ${c.y2 - 20}`;
              const sColor = isSimConn ? '#22D3EE' : (isBroken ? '#EF4444' : (isHL ? '#A855F7' : c.color));
              const sw = isSimConn ? 3 : (isHL ? 2.5 : (c.enabled ? 2 : 1));
              const op = isSimConn ? 0.9 : (isHL ? 0.8 : (active ? (highlightPath.size > 0 ? 0.12 : 0.45) : 0.05));
              return (
                <g key={i} style={{ opacity: op, transition: 'opacity 0.4s' }}>
                  {(isSimConn || isHL) && <path d={path} fill="none" stroke={sColor} strokeWidth={6} opacity={0.12} filter="blur(4px)" />}
                  <path d={path} fill="none" stroke={sColor} strokeWidth={sw} strokeDasharray={isBroken ? '4 3' : (c.enabled ? '' : '6 4')} markerEnd={isSimConn ? 'url(#a-sim)' : (isBroken ? 'url(#a-broken)' : `url(#a-${c.zone})`)} />
                  {c.enabled && active && !isBroken && <circle r="2.5" fill={sColor} opacity="0.6"><animateMotion dur={`${3 + i % 3}s`} repeatCount="indefinite" path={path} /></circle>}
                </g>
              );
            })}
          </svg>

          {/* Branch labels */}
          {conns.map((c, i) => {
            if (!connInZone(c)) return null;
            const isHL = highlightPath.size > 0 && highlightPath.has(c.from) && highlightPath.has(c.to);
            if (highlightPath.size > 0 && !isHL) return null;
            return <BranchLabel key={`bl-${i}`} conn={c} allStageIds={allStageIds} highlighted={isHL} simple={simpleView} />;
          })}

          {/* Stage nodes */}
          {merged.map(stage => {
            if (hiddenNodes.has(stage.stage_id)) return null;
            const p = positions[stage.stage_id];
            if (!p) return null;
            const zone = stage.dashboards?.[0] || 'telepro';
            const color = stage.stage_color || stage.color || ZONE_C[zone] || '#3B82F6';
            const wf = stage.workflow || {};
            const ea = wf.entry_actions || [], et = wf.entry_triggers || [], ex = wf.exit_triggers || [];
            const isConf = ea.length > 0 || ex.length > 0;
            const hasIA = ea.some(a => a.type === 'christelle_ia');
            const isActive = inZone(stage);
            const isExp = expandedNode === stage.stage_id;
            const isDragging = dragging?.id === stage.stage_id;
            const lc = leadCounts[stage.stage_id] || 0;
            const isHL = highlightPath.has(stage.stage_id);
            const isSim = isSimActive && simPathSet.has(stage.stage_id);
            const isSimCur = isSimActive && simPath[simStep] === stage.stage_id;
            const isCollapsed = collapsedNodes.has(stage.stage_id);
            const hc = heatmapOn ? heatColor(lc, maxLeadCount) : null;
            const dimmed = (highlightPath.size > 0 && !isHL) || (isSimActive && !isSim);
            const isInactive = stage.actif === false;

            return (
              <div key={stage.stage_id} data-node className="absolute" style={{
                left: p.x, top: p.y, transform: 'translate(-50%, -50%)',
                zIndex: isExp ? 50 : (isDragging ? 40 : (isSimCur ? 30 : 10)),
                opacity: dimmed ? 0.1 : (isInactive ? 0.25 : (isActive ? 1 : 0.08)), filter: (dimmed || !isActive) ? 'grayscale(1)' : (isInactive ? 'grayscale(0.8)' : 'none'),
                transition: isDragging ? 'none' : 'all 0.4s',
              }}>
                <div data-testid={`eco-node-${stage.stage_id}`}
                  onMouseDown={(e) => { if (e.button === 0 && !isExp) handleNodeDragStart(e, stage.stage_id); }}
                  onClick={() => { if (!dragMoved.current) { setExpandedNode(isExp ? null : stage.stage_id); setEditSec(null); } }}
                  onContextMenu={(e) => { e.preventDefault(); toggleCollapse(stage.stage_id); }}
                  className={`cursor-pointer transition-all duration-200 rounded-xl border backdrop-blur-sm select-none ${isExp ? 'ring-2 ring-purple-400/60 scale-110' : 'hover:scale-105 hover:shadow-xl'} ${isDragging ? 'ring-2 ring-cyan-400/60 scale-110' : ''} ${isSimCur ? 'ring-2 ring-cyan-400 scale-115' : ''}`}
                  style={{
                    background: hc
                      ? `linear-gradient(160deg, ${hc}25, ${hc}08, rgba(15,23,42,0.8))`
                      : (isConf ? `linear-gradient(160deg, ${color}18, ${color}06, rgba(15,23,42,0.8))` : 'rgba(15,23,42,0.8)'),
                    borderColor: isSimCur ? '#22D3EE' : (hc ? `${hc}50` : (isDragging ? '#22D3EE' : (isExp ? `${color}70` : (isHL ? '#A855F780' : `${color}30`)))),
                    boxShadow: isSimCur ? '0 0 40px #22D3EE40, 0 0 80px #22D3EE15' : (hc ? `0 0 20px ${hc}30` : (isDragging ? '0 0 30px #22D3EE40' : (isExp ? `0 0 40px ${color}25` : `0 2px 12px rgba(0,0,0,0.4)`))),
                    minWidth: '140px',
                  }}>
                  <div className="px-3 py-2.5">
                    <div className="flex items-center gap-2 mb-1.5">
                      {!simpleView && <Move className="w-3 h-3 text-slate-600 flex-shrink-0 cursor-grab" />}
                      <div className={`${simpleView ? 'w-2.5 h-2.5' : 'w-3 h-3'} rounded-full flex-shrink-0 ring-2 ring-white/10`} style={{ backgroundColor: hc || color }} />
                      <span className={`${simpleView ? 'text-[11px]' : 'text-[12px]'} font-bold text-white truncate ${simpleView ? 'max-w-[120px]' : 'max-w-[100px]'}`}>{stage.stage_name || stage.nom}</span>
                      {!simpleView && hasIA && <Brain className="w-3.5 h-3.5 text-purple-400 flex-shrink-0" />}
                      {!simpleView && isSimCur && <Activity className="w-3.5 h-3.5 text-cyan-400 animate-pulse flex-shrink-0" />}
                      {simpleView && lc > 0 && <span className="text-[10px] font-bold text-cyan-300">{lc}</span>}
                    </div>
                    {!simpleView && lc > 0 && (
                      <div className="flex items-center gap-1 mb-1">
                        <Users className="w-2.5 h-2.5 text-cyan-400" />
                        <span className={`text-[11px] font-bold ${hc ? '' : 'text-cyan-300'}`} style={hc ? { color: hc } : {}}>{lc}</span>
                        <span className="text-[8px] text-slate-500">leads</span>
                      </div>
                    )}
                    {!simpleView && ea.length > 0 && <div className="flex items-center gap-1 mb-1"><Zap className="w-2.5 h-2.5 text-emerald-500/60" />{ea.map(a => { const ch = CH[a.type]; if (!ch) return null; return <div key={a.id} title={`${ch.label} → ${TGT[a.target] || ''}`} className={`w-5 h-5 rounded-md flex items-center justify-center border ${!a.enabled ? 'opacity-25' : ''}`} style={{ backgroundColor: `${ch.color}15`, borderColor: `${ch.color}30` }}><ch.icon className="w-3 h-3" style={{ color: ch.color }} /></div>; })}</div>}
                    {!simpleView && <div className="flex items-center gap-1">
                      <ArrowDown className="w-2.5 h-2.5 text-amber-500/60" />
                      <span className="text-[9px] text-amber-400/70">{ex.length} sortie{ex.length > 1 ? 's' : ''}</span>
                      {isCollapsed && <span className="text-[9px] text-purple-400 font-mono ml-1">[...]</span>}
                      {!isConf && <span className="text-[9px] text-slate-600 italic ml-1">non configuré</span>}
                    </div>}
                    {/* Edit mode buttons */}
                    {editMode && (
                      <div className="flex gap-1 mt-2 pt-2 border-t border-slate-700/40">
                        <button onClick={(e) => { e.stopPropagation(); setEditingNodeId(stage.stage_id); setEditNodeData({ nom: stage.stage_name || stage.nom, color: color }); }}
                          data-testid={`cockpit-edit-${stage.stage_id}`}
                          className="flex-1 px-2 py-1 bg-violet-600/20 border border-violet-500/30 rounded text-[9px] text-violet-300 hover:bg-violet-600/40 transition-all flex items-center justify-center gap-1">
                          <Settings className="w-2.5 h-2.5" />Éditer
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); if (confirm(`Supprimer "${stage.stage_name || stage.nom}" ?`)) quickDeleteStage(stage.stage_id); }}
                          data-testid={`cockpit-delete-${stage.stage_id}`}
                          className="px-2 py-1 bg-red-600/20 border border-red-500/30 rounded text-[9px] text-red-300 hover:bg-red-600/40 transition-all">
                          <Trash2 className="w-2.5 h-2.5" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                {isExp && <ExpandedPanel stage={stage} color={color} ea={ea} et={et} ex={ex} stages={stages} toggle={toggle} del={del} add={add} setExpandedNode={setExpandedNode} editSec={editSec} setEditSec={setEditSec} />}
                {editingNodeId === stage.stage_id && (
                  <div data-testid={`cockpit-edit-panel-${stage.stage_id}`} data-panel
                    className="absolute top-full left-0 mt-1 bg-slate-900/95 backdrop-blur-xl border border-violet-500/40 rounded-xl p-3 shadow-2xl z-50" style={{ width: 240 }}
                    onClick={e => e.stopPropagation()}>
                    <div className="space-y-2">
                      <input value={editNodeData.nom || ''} onChange={e => setEditNodeData(p => ({ ...p, nom: e.target.value }))}
                        className="w-full bg-slate-800/60 border border-slate-700/40 rounded-lg px-2.5 py-1.5 text-xs text-white focus:outline-none focus:ring-1 focus:ring-violet-500/50" />
                      <div className="flex gap-1">
                        {['#3B82F6','#8B5CF6','#10B981','#F59E0B','#EF4444','#06B6D4','#EC4899','#6B7280'].map(c => (
                          <button key={c} onClick={() => setEditNodeData(p => ({ ...p, color: c }))}
                            className={`w-5 h-5 rounded border-2 transition-all ${editNodeData.color === c ? 'border-white scale-110' : 'border-transparent opacity-60 hover:opacity-100'}`}
                            style={{ backgroundColor: c }} />
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <button onClick={quickEditStage}
                          className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white text-[10px] font-semibold py-1.5 rounded-lg flex items-center justify-center gap-1">
                          <Save className="w-3 h-3" />Sauvegarder
                        </button>
                        <button onClick={() => setEditingNodeId(null)}
                          className="px-2 py-1.5 bg-slate-700 text-slate-400 text-[10px] rounded-lg hover:bg-slate-600">Annuler</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* ORPHAN PANEL */}
        {showOrphanPanel && orphanStages.length > 0 && (
          <div data-testid="orphan-panel" className="absolute top-3 right-3 w-64 max-h-[70vh] bg-slate-900/95 backdrop-blur-xl border border-amber-500/30 rounded-2xl shadow-2xl z-50 overflow-hidden flex flex-col">
            <div className="px-4 py-3 border-b border-slate-700/40 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Unlink className="w-4 h-4 text-amber-400" />
                <span className="text-sm font-bold text-white">Étapes orphelines</span>
              </div>
              <button onClick={() => setShowOrphanPanel(false)} className="p-1 hover:bg-slate-700/50 rounded-lg"><X className="w-3.5 h-3.5 text-slate-400" /></button>
            </div>
            <p className="px-4 py-2 text-[10px] text-slate-500">Non rattachées au flux principal. Activez/désactivez leur visibilité.</p>
            <div className="flex-1 overflow-y-auto px-3 pb-3 space-y-1.5">
              {orphanStages.map(s => {
                const color = s.stage_color || s.color || '#6B7280';
                const isActif = s.actif !== false;
                const lc = leadCounts[s.stage_id] || 0;
                return (
                  <div key={s.stage_id} data-testid={`orphan-${s.stage_id}`}
                    className={`rounded-xl border p-2.5 transition-all ${isActif ? 'border-slate-600/40 bg-slate-800/40' : 'border-slate-700/20 bg-slate-800/20 opacity-50'}`}>
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                      <span className={`text-[11px] font-medium flex-1 truncate ${isActif ? 'text-white' : 'text-slate-500 line-through'}`}>{s.stage_name || s.nom}</span>
                      {lc > 0 && <span className="text-[9px] text-cyan-400 font-bold">{lc}</span>}
                      <button
                        data-testid={`orphan-toggle-${s.stage_id}`}
                        onClick={() => toggleStageActif(s.stage_id, isActif)}
                        className={`p-1 rounded-lg transition-all ${isActif ? 'text-emerald-400 hover:bg-emerald-500/20' : 'text-slate-600 hover:bg-slate-700/50'}`}
                        title={isActif ? 'Désactiver' : 'Activer'}>
                        {isActif ? <Power className="w-3.5 h-3.5" /> : <Power className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                    <div className="mt-1 flex items-center gap-2">
                      <span className="text-[9px] text-slate-600">{s.categorie || '—'}</span>
                      <span className={`text-[8px] px-1.5 py-0.5 rounded-full ${isActif ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'}`}>
                        {isActif ? 'actif' : 'inactif'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* MINIMAP */}
        <Minimap positions={positions} conns={conns} canvasW={baseLayout.w} canvasH={baseLayout.h} view={view} containerW={containerSize.w} containerH={containerSize.h} />

        {/* SIM PANEL */}
        {isSimActive && (
          <SimPanel simPath={simPath} simStep={simStep} merged={merged}
            onStep={(d) => setSimStep(prev => Math.max(0, Math.min(simPath.length - 1, prev + d)))}
            onPlay={() => setSimPlaying(p => !p)} onStop={stopSim} playing={simPlaying} />
        )}
      </div>
    </div>
  );
};

export default WorkflowCockpit2D;
